// @ts-nocheck
import PriceSpiderWhereToBuy, {
    PriceSpiderWhereToBuyStyles,
} from "@store/Library/Components/Foundation/PriceSpiderWhereToBuy";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const enum fields {
    ctaType = "ctaType",
    ctaText = "ctaText",
    additionnalText = "additionnalText",
    isInvertedTextColor = "isInvertedTextColor",
    buttonVariant = "buttonVariant",
    modalSizeVariant = "modalSizeVariant",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.ctaType]: "button" | "link";
        [fields.ctaText]: string;
        [fields.isInvertedTextColor]: boolean;
        [fields.additionnalText]: string;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.modalSizeVariant]: "small" | "medium" | "large";
    };
}

type Props = OwnProps;

export interface PriceSpiderModalTogglerStyles {
    wrapper?: InjectableCss;
    additionalText?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    link?: LinkPresentationProps;
    extendedStyles?: PriceSpiderWhereToBuyStyles;
}

export const modalTogglerStyles: PriceSpiderModalTogglerStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${pxToRem(10)};
        `,
    },
};

const styles = modalTogglerStyles;

const PriceSpiderModalToggler: FC<Props> = ({ id, fields }) => {
    const { priceSpiderGenericKey } = useSelector(
        (state: ApplicationState) => state.context.settings.settingsCollection.bunzlSettings!,
    );
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    if (!priceSpiderGenericKey) {
        return null;
    }

    const openModalHandler = () => {
        setModalIsOpen(true);
    };

    const closeModalHandler = () => {
        setModalIsOpen(false);
    };

    const buttonStyles: PriceSpiderModalTogglerStyles["button"] = {
        ...styles.button,
        variant: fields.buttonVariant,
    };

    const textStyles: PriceSpiderModalTogglerStyles["additionalText"] = mergeProps(styles.additionalText, {
        css: css`
            color: ${fields.isInvertedTextColor ? getColor("primary.contrast") : null};
        `,
    });

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                {fields.additionnalText && <Typography {...textStyles}>{fields.additionnalText}</Typography>}
                {fields.ctaType === "button" && (
                    <Button onClick={openModalHandler} {...buttonStyles}>
                        {fields.ctaText}
                    </Button>
                )}
                {fields.ctaType === "link" && (
                    <Link onClick={openModalHandler} {...styles.link}>
                        {fields.ctaText}
                    </Link>
                )}
            </StyledWrapper>
            <Modal
                isOpen={modalIsOpen}
                handleClose={closeModalHandler}
                headline={fields.ctaText}
                sizeVariant={fields.modalSizeVariant}
            >
                <PriceSpiderWhereToBuy
                    type="Generic"
                    extendedStyles={styles.extendedStyles}
                    modalIsOpen={modalIsOpen}
                />
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: PriceSpiderModalToggler,
    definition: {
        group: "Common",
        displayName: "Price Spider - Generic Modal",
        icon: "Banner",
        allowedContexts: ["HomePage"],
        fieldDefinitions: [
            {
                name: fields.ctaType,
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Button", value: "button" },
                    { displayName: "Link", value: "link" },
                ],
                defaultValue: "button",
                hideEmptyOption: true,
            },
            {
                name: fields.ctaText,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.additionnalText,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.isInvertedTextColor,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.buttonVariant,
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.ctaType === "button",
            },
            {
                name: fields.modalSizeVariant,
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Large", value: "large" },
                ],
                defaultValue: "medium",
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
